/* @flow */
import initializeJWPlayer from './initializeJWPlayer';
import { loadScriptInHead } from '../dom';
import {invoke, includes} from 'lodash';
import type { AdData } from '../ads/adUnit';

export type JWPlayerInstance = {
  getState: () => 'buffering'|'idle'|'paused'|'playing',
  on: Function,
  pause: Function,
  play: Function,
  remove: Function,
  setup: Function
};

export type Handlers = {
  adPause: Function,
  adPlay: Function,
  pause: Function,
  play: Function,
  complete: Function,
  buffer: Function,
  ready: Function
};

export type Props = {
  adsState: { ad_data: AdData },
  content: { jw_video_id: string },
  intl: { formatMessage: Function },
  isFirstSection: bool,
  isMobileSlideSection: bool,
  isDesktopSlideSection: bool,
  sold_content_type: ?string,
  sanitizedTitle: string
};

type EditionConfig = {
  jw_player_id: string
};

export type EditionData = {
  config: EditionConfig
};

export type LocationData = {
  query: {
    test: ?string
  }
};

const getJWScriptUrl =
  (jw_player_id: string): string =>
    `https://content.jwplatform.com/libraries/${jw_player_id}.js`;

export const jwPlayerIsInitialized = (): bool =>
  typeof window.jwplayer !== 'undefined';

export const getJWPlayerContainerID =
  ({ content: { jw_video_id } }: Props): string =>
    `jw-player-${jw_video_id}`;

const getJWPlayerInstance =
  (props: Props): JWPlayerInstance|void =>
    window.jwplayer && window.jwplayer(getJWPlayerContainerID(props));

const loadOrFetchJWPlayer = (props: Props, edition_data: EditionData): Promise<JWPlayerInstance> =>
  new Promise((resolve: Function): void => {
    if (jwPlayerIsInitialized()) {
      resolve(getJWPlayerInstance(props));
      return;
    }
    loadScriptInHead(
      'necessary',
      getJWScriptUrl(edition_data.config.jw_player_id),
      (): void => resolve(getJWPlayerInstance(props))
    );
  });

export const loadOrInitializeJWPlayer =
  (props: Props, handlers: Handlers, edition_data: EditionData, location: LocationData): Promise<JWPlayerInstance> =>
    loadOrFetchJWPlayer(props, edition_data)
      .then((jwPlayerInstance: JWPlayerInstance): JWPlayerInstance => {
        initializeJWPlayer(jwPlayerInstance, props, handlers, edition_data, location);
        return jwPlayerInstance;
      });

export const pauseJWPlayer = (props: Props): void => {
  const jwPlayerInstance = getJWPlayerInstance(props);
  if (typeof jwPlayerInstance === 'undefined') {
    return;
  }
  jwPlayerInstance.pause();
};

export const removeJWPlayer = (props: Props): void => {
  const jwPlayerInstance = getJWPlayerInstance(props);
  if (typeof jwPlayerInstance === 'undefined') {
    return;
  }
  jwPlayerInstance.remove();
};

export const isJWVideoPlaying = (props: Props): bool => {
  const jwPlayerInstance = getJWPlayerInstance(props);
  if (typeof jwPlayerInstance === 'undefined') {
    return false;
  }
  return includes(
    ['playing', 'buffering'],
    invoke(jwPlayerInstance, 'getState')
  );
};
